import { useEffect, useRef, useState } from "react";
import { Editor, ReactRenderer } from "@tiptap/react";
import { MentionInputMentionList } from "./MentionInputMentionList.tsx";
import tippy, { Instance as TippyInstance } from "tippy.js";
import { MentionInputProps, MentionRef } from "./MentionInput.tsx";
import { useSearchResultList } from "../search/SearchResultList.hooks.ts";
import { useRelationHook } from "../../hooks/Relations.hooks.ts";
import { CustomMention } from "./MentionExtentions.ts";
import { PluginKey } from "@tiptap/pm/state";
import { RelationViewModel } from "../../../taskManagement/createTaskPane/CreateTaskPane.types.ts";
import { isNullOrWhitespace } from "../../stringOperations.ts";
import { EntityType } from "../../Common.types.ts";
import { containsHtmlTags } from "../../utilities.ts";

// A dropdown below the field opens on the mousedown that blurred it, so it is placed against the expanded layout and
// left hanging the toolbar's height too low once the field shrinks on release. Poppers and popovers only reposition
// on scroll and resize, so a resize once the shrink is rendered brings them back to their anchor.
function repositionPopupsAfterCollapse() {
    requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
}

export function useMentionHook(props: MentionInputProps, disableMentions: boolean) {
    const [hasFocus, setHasFocus] = useState(false);
    const [toolbarFocus, setToolbarFocus] = useState(false);
    const mouseButtonIsDown = useRef(false);
    const pendingCollapse = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        const onMouseDown = () => (mouseButtonIsDown.current = true);
        const onMouseUp = () => (mouseButtonIsDown.current = false);
        document.addEventListener("mousedown", onMouseDown, true);
        document.addEventListener("mouseup", onMouseUp, true);

        return () => {
            document.removeEventListener("mousedown", onMouseDown, true);
            document.removeEventListener("mouseup", onMouseUp, true);
            pendingCollapse.current?.();
        };
    }, []);

    // The editor blurs on mousedown, while the button is still held. Hiding the toolbar there shrinks the field,
    // so anything below it moves out from under the pointer and mouseup lands somewhere else - the browser then
    // fires click on the common ancestor of the two, and the button that was pressed never receives it. Holding
    // the toolbar until the button is released keeps the layout still for the whole press. A blur with no button
    // down (tabbing away, Escape) has no click coming, so it collapses immediately as before.
    const collapseToolbar = () => {
        if (!mouseButtonIsDown.current) {
            setHasFocus(false);
            return;
        }

        const collapseOnRelease = () => {
            pendingCollapse.current = undefined;
            setHasFocus(false);
            repositionPopupsAfterCollapse();
        };

        pendingCollapse.current = () => document.removeEventListener("mouseup", collapseOnRelease, true);
        document.addEventListener("mouseup", collapseOnRelease, { once: true, capture: true });
    };

    const { isLoading, entitySearchSuggestions, fetchSuggestions, onShowMoreForEntityTypeClick, setShowMoreForEntityType, isEntitySpecificSearch } =
        useSearchResultList(true); // Fetch dynamic data
    const mentionSource = props.mentionSource as { entityFromId: string; entityFromType: EntityType } | undefined;
    const { onItemSelected } = useRelationHook(mentionSource?.entityFromId ?? "", mentionSource?.entityFromType ?? EntityType.ProcessingActivity);

    const componentRef = useRef<ReactRenderer<MentionRef> | undefined>(undefined); // Updated type
    const popupRef = useRef<TippyInstance | undefined>(undefined);

    const { manageRelationsExternal } = props;

    const popperPlacement = props.popperPlacement ? props.popperPlacement : "bottom-start";

    function getWhitespaceCount(query: string) {
        const matches = query?.match(/([\s]+)/g);
        return matches?.length || 0;
    }

    const suggestionConfig = {
        allowSpaces: true,
        items: async ({ query }: { query: string }) => {
            // We dont await the fetch. If the fetch is awaited, then the popover won't show until data is loaded
            fetchSuggestions(query);

            // Return empty because we're handling rendering manually
            return entitySearchSuggestions;
        },
        render: () => {
            return {
                onStart: async (props: any) => {
                    if (props.query.charAt(0) === " ") {
                        return;
                    }

                    // don't render component if space between the search query words is greater than 4
                    const whitespaceCount = getWhitespaceCount(props.query);
                    if (whitespaceCount > 0) {
                        return;
                    }

                    componentRef.current = new ReactRenderer(MentionInputMentionList, {
                        props: {
                            ...props,
                            items: [], // Start with an empty list
                            isLoading: true, // Start with a loading state
                            onItemSelected: manageRelationsExternal ? () => {} : onItemSelected,
                            onShowMoreClick: (entity: EntityType) => {
                                onShowMoreForEntityTypeClick(props.query, entity);
                            },
                            isEntitySpecificSearch: isEntitySpecificSearch,
                        },
                        editor: props!.editor,
                    });

                    popupRef.current = tippy(document.body, {
                        getReferenceClientRect: props!.clientRect,
                        appendTo: () => document.body,
                        content: componentRef.current.element,
                        showOnCreate: props.editor.view.focused, // Always show popup immediately
                        interactive: true,
                        trigger: "manual",
                        placement: popperPlacement,
                    });
                },

                onUpdate: (props: any) => {
                    componentRef.current?.updateProps({
                        ...props,
                        // Make sure onShowMoreClick uses the latest query
                        onShowMoreClick: (entity: EntityType) => onShowMoreForEntityTypeClick(props.query, entity),
                    });

                    popupRef.current?.setProps({
                        getReferenceClientRect: props!.clientRect,
                    });
                },

                onKeyDown: (props: any) => {
                    if (props!.event.key === "Escape") {
                        closeSuggestions();
                        return true;
                    }

                    if (componentRef.current && componentRef.current.ref) {
                        return componentRef.current.ref.onKeyDown(props);
                    }

                    return false;
                },

                onExit: () => {
                    closeSuggestions();
                },
            };
        },
    };

    function closeSuggestions() {
        if (popupRef.current) {
            popupRef.current?.destroy();
            popupRef.current = undefined;
        }

        if (componentRef.current) {
            componentRef.current?.destroy();
            componentRef.current = undefined;
        }
        setShowMoreForEntityType(undefined);
    }

    const AtMention = CustomMention("@", props.linkColor).configure({
        HTMLAttributes: {
            class: "mention",
        },
        suggestion: {
            ...suggestionConfig,
            pluginKey: new PluginKey("@"),
            char: disableMentions ? "" : "@",
        },
    });

    const HashMention = CustomMention("#", props.linkColor).configure({
        HTMLAttributes: {
            class: "mention",
        },
        suggestion: {
            ...suggestionConfig,
            pluginKey: new PluginKey("#"),
            char: disableMentions ? "" : "#",
        },
    });

    const SlashMention = CustomMention("/", props.linkColor).configure({
        HTMLAttributes: {
            class: "mention",
        },
        suggestion: {
            ...suggestionConfig,
            pluginKey: new PluginKey("/"),
            char: disableMentions ? "" : "/",
        },
    });

    // Update ReactRenderer props dynamically when state changes (suggestions, isLoading)
    useEffect(() => {
        if (componentRef.current) {
            componentRef.current.updateProps({
                items: entitySearchSuggestions || [], // Update suggestions
                isLoading: isLoading, // Update loading state
                isEntitySpecificSearch: isEntitySpecificSearch,
            });
        }
        // eslint-disable-next-line
    }, [entitySearchSuggestions, isLoading]); // Re-run whenever these states change

    return {
        hasFocus,
        setHasFocus,
        collapseToolbar,
        isLoading,
        suggestions: entitySearchSuggestions,
        fetchSuggestions,
        componentRef,
        AtMention,
        HashMention,
        SlashMention,
        toolbarFocus,
        setToolbarFocus,
    };
}

export function setEditorContent(editor: Editor, content: string | undefined) {
    if (isNullOrWhitespace(content)) {
        editor.commands.setContent("", { emitUpdate: false });
        return;
    }

    // Plain text with literal newlines (from `<textarea>`-style input) needs `\n` -> `<br/>` to
    // render line breaks. Markup already encodes its own breaks, so injecting `<br/>` between
    // block elements (e.g. server-formatted `<p>\n<ul>`) corrupts the structure — TipTap then
    // renormalizes the corrupted HTML to something that never matches `props.value`, which keeps
    // the sync effect firing setContent repeatedly.
    const htmlContent = containsHtmlTags(content!) ? content! : content!.split("\n").join("<br/>");
    editor.commands.setContent(htmlContent, { emitUpdate: false });
}

export function getRelationsFromMentions(editor: Editor): Array<RelationViewModel> {
    const mentions: Array<RelationViewModel> = [];

    const mentionTypes = ["mention@", "mention/", "mention#"];
    editor.state.doc.descendants((node: any) => {
        if (mentionTypes.includes(node.type.name)) {
            mentions.push({
                entityId: node.attrs.id,
                entityType: Number(node.attrs.entityType),
                entityName: node.attrs.label,
                parentId: node.attrs.parentId,
                relationId: "",
            });
        }
    });

    return mentions;
}
